import fs from "fs/promises";
import path from "path";
import chalk from "chalk";
import inquirer from "inquirer";

export interface PromoteEnvOptions {
  from: string;
  to: string;
  mode: "add" | "remove" | "comment" | "example";
  interactive: boolean;
  dryRun?: boolean;
  backup?: boolean;
}

function parseEnvFile(content: string) {
  // Returns array of { key, value, raw, comment, isCommented }
  const lines = content.split(/\r?\n/);
  const entries: Array<{
    key: string;
    value: string;
    raw: string;
    comment?: string;
    isCommented: boolean;
  }> = [];
  let lastComment: string | undefined = undefined;
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      lastComment = undefined;
      continue;
    }
    if (trimmed.startsWith("#")) {
      lastComment = trimmed;
      continue;
    }
    // Support commented out vars
    const uncommented = trimmed.startsWith("#")
      ? trimmed.slice(1).trim()
      : trimmed;
    const isCommented = trimmed.startsWith("#");
    const eqIdx = uncommented.indexOf("=");
    if (eqIdx === -1) continue;
    const key = uncommented.slice(0, eqIdx).trim();
    const value = uncommented.slice(eqIdx + 1).trim();
    entries.push({ key, value, raw: line, comment: lastComment, isCommented });
    lastComment = undefined;
  }
  return entries;
}

function serializeEnv(
  entries: Array<{
    key: string;
    value: string;
    raw: string;
    comment?: string;
    isCommented: boolean;
  }>
) {
  // Reconstruct .env file from entries
  return entries
    .map(
      (e) =>
        (e.comment ? `${e.comment}\n` : "") +
        (e.isCommented ? "# " : "") +
        `${e.key}=${e.value}`
    )
    .join("\n");
}

export async function promoteEnv(options: Partial<PromoteEnvOptions>) {
  const {
    from = ".env.development",
    to = ".env.production",
    mode = "add",
    interactive = false,
    dryRun = false,
    backup = false,
  } = options;

  // Read both env files
  let fromContent: string, toContent: string;
  try {
    fromContent = await fs.readFile(from, "utf-8");
  } catch (e) {
    throw new Error(`Could not read source env file: ${from}`);
  }
  try {
    toContent = await fs.readFile(to, "utf-8");
  } catch (e) {
    toContent = "";
  }

  const fromEntries = parseEnvFile(fromContent);
  const toEntries = parseEnvFile(toContent);
  const fromKeys = new Set(fromEntries.map((e) => e.key));
  const toKeys = new Set(toEntries.map((e) => e.key));

  // Compute diff
  const onlyInFrom = fromEntries.filter((e) => !toKeys.has(e.key));
  const onlyInTo = toEntries.filter((e) => !fromKeys.has(e.key));
  const inBoth = fromEntries.filter((e) => toKeys.has(e.key));

  let updatedEntries = [...toEntries];
  let summary: string[] = [];

  // Interactive mode: prompt before each change
  async function confirmAction(message: string, def = true) {
    if (!interactive) return true;
    const { ok } = await inquirer.prompt([
      { type: "confirm", name: "ok", message, default: def },
    ]);
    return ok;
  }

  if (mode === "add" || mode === "example") {
    for (const entry of onlyInFrom) {
      if (await confirmAction(`Add variable ${entry.key} to ${to}?`)) {
        updatedEntries.push({
          ...entry,
          isCommented: mode === "example",
          value: mode === "example" ? "" : entry.value,
        });
        summary.push(`Added: ${entry.key}`);
      }
    }
  }
  if (mode === "remove") {
    const newEntries = [];
    for (const e of updatedEntries) {
      if (!fromKeys.has(e.key)) {
        if (
          await confirmAction(`Remove variable ${e.key} from ${to}?`, false)
        ) {
          summary.push(`Removed: ${e.key}`);
          continue; // skip this entry (remove)
        }
      }
      newEntries.push(e);
    }
    updatedEntries = newEntries;
  }
  if (mode === "comment") {
    const newEntries = [];
    for (const e of updatedEntries) {
      if (!fromKeys.has(e.key)) {
        if (
          await confirmAction(`Comment out variable ${e.key} in ${to}?`, false)
        ) {
          summary.push(`Commented out: ${e.key}`);
          newEntries.push({ ...e, isCommented: true });
          continue;
        }
      }
      newEntries.push(e);
    }
    updatedEntries = newEntries;
  }
  if (mode === "example") {
    const newEntries = [];
    for (const e of updatedEntries) {
      if (!fromKeys.has(e.key)) {
        if (
          await confirmAction(`Comment out variable ${e.key} in ${to}?`, false)
        ) {
          summary.push(`Commented out: ${e.key}`);
          newEntries.push({ ...e, isCommented: true, value: "" });
          continue;
        }
        newEntries.push(e);
        continue;
      }
      newEntries.push({ ...e, value: "" });
    }
    updatedEntries = newEntries;
  }

  // Remove duplicates (keep first occurrence)
  const seen = new Set<string>();
  updatedEntries = updatedEntries.filter((e) => {
    if (seen.has(e.key)) return false;
    seen.add(e.key);
    return true;
  });

  // DRY RUN: Show what would change, do not write
  if (dryRun) {
    console.log(chalk.yellow("[Dry Run] No changes will be written."));
    console.log(serializeEnv(updatedEntries));
    return;
  }

  // BACKUP: Save a backup of the target file
  if (backup && toContent) {
    const backupPath = to + ".bak";
    await fs.writeFile(backupPath, toContent, "utf-8");
    console.log(chalk.gray(`Backup created at ${backupPath}`));
  }

  // Write updated target file
  await fs.writeFile(to, serializeEnv(updatedEntries) + "\n", "utf-8");

  // Print summary
  console.log(
    chalk.green(`Promoted variables from ${from} to ${to} in mode: ${mode}`)
  );
  if (summary.length) {
    summary.forEach((line) => console.log(chalk.blue(line)));
  } else {
    console.log(chalk.gray("No changes needed."));
  }
}
